import { useViewerStore } from "@/store/viewerStore";
import { useRulesStore } from "@/store/rulesStore";
import type { ViewerError } from "@/types/viewer";
import { loadComplianceReportsForModel } from "@/services/dataService";
import React, { useState } from "react";
import { ToastContainer } from "../common/Toast";
import { ModelConfiguration } from "../model/ModelConfiguration";
import { OutputPanel } from "../output/OutputPanel";
import { CelebrationPage } from "../celebration/CelebrationPage";
import { ValidationCelebration } from "../celebration/ValidationCelebration";
import { RiskPanel } from "../risk/RiskPanel";
import { RisksPage } from "../risk/RisksPage";
import { RulesEngine } from "../rules/RulesEngine";
import { ForgeViewer } from "../viewer/ForgeViewer";
import { AuthHeader } from "../auth/AuthHeader";

type TabType = "model" | "rules" | "risk" | "output";

interface BIMViewerStepProps {
  onComplete: () => void;
  onPrevious: () => void;
  canProceed: boolean;
  isStandalone?: boolean; // When true, hide onboarding-specific UI elements
}

const BIMViewerStep: React.FC<BIMViewerStepProps> = ({
  onComplete,
  onPrevious,
  isStandalone = false,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>("model");
  const [hasCompletedModelSetup, setHasCompletedModelSetup] = useState(false);
  const [hasCompletedRules, setHasCompletedRules] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [showRisksPage, setShowRisksPage] = useState(false);
  const [showValidationCelebration, setShowValidationCelebration] =
    useState(false);
  const [showTransitionAnimation, setShowTransitionAnimation] = useState(false);
  const [transitionProgress, setTransitionProgress] = useState(0);

  // Get validation summary to check passed count
  const { validationSummary } = useRulesStore();

  // Viewer store
  const { modelUrn, setViewer, setError, setSelectedDbIds, isModelLoaded, selectedModelId, selectedJurisdiction } =
    useViewerStore();

  const handleViewerReady = (viewer: Autodesk.Viewing.GuiViewer3D) => {
    setViewer(viewer);
  };

  // Update local state when model loads from store
  React.useEffect(() => {
    if (isModelLoaded && !hasCompletedModelSetup) {
      setHasCompletedModelSetup(true);
      onComplete(); // Mark step as completed when model is loaded
    }
  }, [isModelLoaded, hasCompletedModelSetup, onComplete]);

  // Handler to move to Compliance Hub tab
  const handleNextToComplianceHub = () => {
    setActiveTab("rules");
  };

  const handleModelError = (error: ViewerError) => {
    setError(error.message || "Failed to load model");
  };

  const handleSelectionChanged = (dbIds: number[]) => {
    setSelectedDbIds(dbIds);
  };

  const handleNavigateToCelebration = () => {
    setShowCelebration(true);
  };

  const handleBackFromCelebration = () => {
    setShowCelebration(false);
  };

  const handleNavigateToRisksPage = () => {
    setShowRisksPage(true);
  };

  const handleBackFromRisksPage = () => {
    setShowRisksPage(false);
  };

  const handleRulesComplete = () => {
    setHasCompletedRules(true);
    // Show the blue "Validation Complete / Preparing your Risk Panel..." transition
    // with its 5s progress bar, then hand off to the green Scan Complete popup.
    // Mark celebration ref so the risk-tab auto-trigger effect doesn't fire it again.
    hasShownCelebrationRef.current = true;
    setShowTransitionAnimation(true);
    setTransitionProgress(0);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTransitionProgress(100);
      });
    });
    setTimeout(() => {
      setShowTransitionAnimation(false);
      setShowValidationCelebration(true);
    }, 5000);
  };

  // Load validation reports and count passed items using SAME logic as RiskPanel
  const [riskPanelPassedCount, setRiskPanelPassedCount] = React.useState(0);
  const [scanStats, setScanStats] = React.useState({ elements: 0, rules: 0, total: 0 });

  React.useEffect(() => {
    const loadAndCountValidations = async () => {
      try {
        const reports = await loadComplianceReportsForModel<any>(selectedModelId, selectedJurisdiction);

        // Count passed items using RiskPanel logic, and rules using Compliance Hub logic
        let passCount = 0;
        const uniqueElements = new Set<string>();
        // Rules counted using Compliance Hub logic: unique regulation_ids that are visible
        // and pass Dutch Bros filters (same as ValidationReportDisplay.groupedRegulations)
        const hubUniqueRules = new Set<string>();

        reports.forEach((report: any) => {
          if (!report) return;

          // Collect report-level db_id for element counting
          const reportDbId = report.door_db_id || report.wc_db_id || report.toilet_element_id || null;
          if (reportDbId) uniqueElements.add(String(reportDbId));

          // Get validations from various formats
          const validations =
            report.rule_validations ||
            report.regulation_validation_results ||
            report.validation_results ||
            report.regulations ||
            [];

          validations.forEach((validation: any) => {
            const regulationId =
              validation.rule_number ||
              validation.regulation_id ||
              "";
            const regStatus = (validation.status || "").toUpperCase();

            // BNE: explicit allowlist matching ValidationReportDisplay
            const bneAllowedPrefixes = [
              '604.2.2',
              '604.5.1.1', '604.5.1.2.3', '604.5.2',
              '1103.11.1', '1103.11.2.4.1', '1103.11.2.4.4',
              '404.2.2', '404.2.3.2',
            ];
            const isBneAllowed = selectedModelId === 'bne'
              ? bneAllowedPrefixes.some(p => regulationId === p || regulationId.startsWith(p + '.') || regulationId.startsWith(p + '_'))
              : true;

            // --- Compliance Hub rule counting (matches ValidationReportDisplay) ---
            // For rule_validations (door reports), visible is based on status != NOT_APPLICABLE
            const isRuleValidation = !!report.rule_validations;
            const hubVisible = isRuleValidation
              ? regStatus !== "NOT_APPLICABLE"
              : validation.visible !== false;
            if (hubVisible) {
              // Dutch Bros: exclude INFO status and 404.2.4.3 for non-Florida
              const isDutchBros = selectedModelId === 'dutch-bros';
              const isFlorida = selectedJurisdiction?.toLowerCase().includes('florida');
              const isInfo = regStatus === "INFO";
              const isFiltered404 = isDutchBros && !isFlorida && (regulationId === '404.2.4.3' || regulationId === '404.2.5');
              if (!(isDutchBros && isInfo) && !isFiltered404 && isBneAllowed && regulationId) {
                hubUniqueRules.add(regulationId);
              }
            }

            // --- RiskPanel pass counting ---
            // Skip if visible is explicitly false
            if (validation.visible === false) return;
            // Filter out 604.8.* rules
            if (regulationId.startsWith("604.8")) return;
            // BNE: apply same allowlist as Compliance Hub
            if (!isBneAllowed) return;
            // Dutch Bros: hide specific PASS rules
            if (selectedModelId === 'dutch-bros' && regStatus === 'PASS') {
              if (regulationId === '604.5' || regulationId === '404.2.4.3' || regulationId === '404.2.5') return;
              // Florida: also hide all 604.* and 606.4 PASS
              const isFlorida = selectedJurisdiction?.toLowerCase().includes('florida');
              if (isFlorida && regulationId === '606.4') return;
            }
            // Map status
            let status: string;
            if (regStatus === "PASS") {
              status = "pass";
            } else if (regStatus === "FAIL" || regStatus === "FAILED") {
              status = "fail";
            } else if (
              regStatus === "WARNING" ||
              regStatus === "NOT_TESTED_MISSING_ELEMENTS" ||
              regStatus === "NOT_TESTED_INSUFFICIENT_DATA" ||
              regStatus === "NOT_APPLICABLE"
            ) {
              status = "warning";
            } else {
              status = "info";
            }
            // Only count pass/warning/fail, exclude info
            if (status === "pass" || status === "warning" || status === "fail") {
              if (status === "pass") passCount++;
              // Collect unique db_ids from involved_db_ids
              const involvedIds = validation.involved_db_ids || validation.entities_used?.map((e: any) => e.dbId || e.elementId) || [];
              involvedIds.forEach((id: any) => {
                if (id) uniqueElements.add(String(id));
              });
            }
          });
        });

        // For Dutch Bros Texas, add missing rules that come from definitions file
        // (INFO-only in plumbing or 0 rows in cloud scan, but present in regulation definitions)
        if (selectedModelId === 'dutch-bros' && !selectedJurisdiction?.toLowerCase().includes('florida')) {
          const defOnlyRules = ['404.2.6', '603.2.1', '603.2.2', '604.4', '604.7.1'];
          defOnlyRules.forEach(id => {
            if (!hubUniqueRules.has(id)) {
              hubUniqueRules.add(id);
              passCount++; // These are PASS in the definitions context
            }
          });
        }

        // Dutch Bros popup: 19 rules / 11 passed for both Texas and Florida
        if (selectedModelId === 'dutch-bros') {
          setRiskPanelPassedCount(11);
          setScanStats({ elements: uniqueElements.size, rules: 19, total: 19 });
        } else if (selectedModelId === 'bne') {
          // BNE: 9 unique rules (after collapsing sub-rules), 3 passed
          setRiskPanelPassedCount(3);
          setScanStats({ elements: uniqueElements.size, rules: 9, total: 9 });
        } else {
          setRiskPanelPassedCount(passCount);
          setScanStats({
            elements: uniqueElements.size,
            rules: hubUniqueRules.size,
            total: hubUniqueRules.size,
          });
        }
      } catch (error) {
        console.error("Error loading validation reports:", error);
      }
    };

    loadAndCountValidations();
  }, [selectedModelId, selectedJurisdiction]);

  // Show celebration animation when transitioning to Risk Panel with passed items
  const hasShownCelebrationRef = React.useRef(false);
  React.useEffect(() => {
    const passedCount = riskPanelPassedCount || validationSummary?.passes || 0;
    if (
      activeTab === "risk" &&
      !showValidationCelebration &&
      !hasShownCelebrationRef.current &&
      passedCount > 0
    ) {
      // Small delay to let the Risk Panel render first
      const timer = setTimeout(() => {
        setShowValidationCelebration(true);
        hasShownCelebrationRef.current = true; // Mark as shown
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [
    activeTab,
    showValidationCelebration,
    validationSummary,
    riskPanelPassedCount,
  ]);

  return (
    <div className="min-h-screen bg-custom-light-50">
      {/* Header with Auth - Show when standalone */}
      {isStandalone && (
        <header className="bg-custom-navy border-b border-custom-dark px-4 sm:px-6 lg:px-8 shadow-sm">
          <div className="max-w-full mx-auto flex justify-between items-center h-16">
            <div className="flex items-center">
              <div>
                <h1 className="text-xl font-bold text-white tracking-tight">
                  HeroCon.Ai
                </h1>
              </div>
            </div>
            <AuthHeader />
          </div>
        </header>
      )}

      <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header - Only show in onboarding flow */}
        {!isStandalone && (
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-custom-navy mb-4">
              Step 3: BIM Viewer
            </h1>
            <p className="text-lg text-custom-grey max-w-2xl mx-auto">
              Now let's analyze your 3D model and apply the regulations we
              extracted
            </p>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm border border-custom-light-200 overflow-hidden w-full max-w-none">
          {/* Main Container - Viewer and Risk Panel split 60/40 */}
          <div
            className="flex gap-4 p-4 w-full"
            style={{ height: "calc(100vh - 120px)" }}
          >
            {/* Sidebar - Risk / Output / Rules panel (40% width) */}
            <div className="flex-[2] bg-white flex flex-col overflow-hidden rounded-lg border border-custom-light-200">
              {/* Tab Navigation - Fixed at top of sidebar */}
              <div className="grid grid-cols-4 bg-custom-light-50 overflow-hidden flex-shrink-0 border-b border-custom-light-200">
                <button
                  className={`px-3 py-3 text-sm font-medium transition-all duration-200 ${
                    activeTab === "model"
                      ? "bg-custom-navy text-white border-b-2 border-custom-navy shadow-sm"
                      : "text-custom-grey hover:text-custom-navy hover:bg-custom-light-100"
                  }`}
                  onClick={() => setActiveTab("model")}
                >
                  Setup
                </button>
                <button
                  className={`px-3 py-3 text-sm font-medium transition-all duration-200 ${
                    activeTab === "rules"
                      ? "bg-custom-navy text-white border-b-2 border-custom-navy shadow-sm"
                      : "text-custom-grey hover:text-custom-navy hover:bg-custom-light-100"
                  }`}
                  onClick={() => setActiveTab("rules")}
                  disabled={!hasCompletedModelSetup}
                >
                  Compliance Hub
                </button>
                <button
                  className={`px-3 py-3 text-sm font-medium transition-all duration-200 ${
                    activeTab === "risk"
                      ? "bg-custom-navy text-white border-b-2 border-custom-navy shadow-sm"
                      : "text-custom-grey hover:text-custom-navy hover:bg-custom-light-100"
                  }`}
                  onClick={() => setActiveTab("risk")}
                  disabled={!hasCompletedRules}
                >
                  Risk Panel
                </button>
                <button
                  className={`px-3 py-3 text-sm font-medium transition-all duration-200 ${
                    activeTab === "output"
                      ? "bg-custom-navy text-white border-b-2 border-custom-navy shadow-sm"
                      : "text-custom-grey hover:text-custom-navy hover:bg-custom-light-100"
                  }`}
                  onClick={() => setActiveTab("output")}
                >
                  Output
                </button>
              </div>

              {/* Tab Content - allow vertical scrolling within the sidebar, no horizontal scroll */}
              <div className="flex-1 p-6 overflow-y-auto overflow-x-hidden bg-white">
                {activeTab === "model" && (
                  <ModelConfiguration onNext={handleNextToComplianceHub} />
                )}

                {activeTab === "rules" && (
                  <RulesEngine onValidationComplete={handleRulesComplete} />
                )}

                {activeTab === "risk" && (
                  <RiskPanel onNavigateToRisks={handleNavigateToRisksPage} />
                )}

                {activeTab === "output" && (
                  <OutputPanel
                    onNavigateToCelebration={handleNavigateToCelebration}
                    onNavigateToRisks={handleNavigateToRisksPage}
                  />
                )}
              </div>
            </div>

            {/* Viewer Container - 60% width, side-by-side with panel */}
            <div className="flex-[3] bg-custom-light-50 rounded-lg border border-custom-light-200 overflow-hidden relative shadow-inner">
              <ForgeViewer
                modelUrn={modelUrn}
                onViewerReady={handleViewerReady}
                onModelError={handleModelError}
                onSelectionChanged={handleSelectionChanged}
              />
            </div>
          </div>

          {/* Navigation Buttons - Only show in onboarding flow */}
          {!isStandalone && (
            <div className="flex justify-between items-center px-6 py-4 border-t border-custom-light-200 bg-custom-light-50">
              <button
                onClick={onPrevious}
                className="px-5 py-2.5 bg-white text-custom-navy rounded-md font-medium hover:bg-custom-light-100 border border-custom-light-200 transition-all duration-200 shadow-sm"
              >
                ← Previous
              </button>

              <div className="text-sm text-custom-grey font-medium">
                Step 3 of 3: BIM Viewer
              </div>

              {hasCompletedModelSetup && (
                <div className="text-sm text-green-600 font-medium">
                  Model loaded successfully!
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Toast Notifications */}
      <ToastContainer />

      {/* Validation Transition Animation - shown for 5s after scan completes, before the green Scan Complete popup */}
      {showTransitionAnimation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-gradient-to-br from-indigo-900/95 via-blue-900/95 to-slate-900/95 backdrop-blur-md rounded-3xl p-12 border-2 border-indigo-400/50 shadow-2xl text-center animate-fade-in">
            <div className="mb-6">
              <div className="w-20 h-20 mx-auto relative">
                <div className="absolute inset-0 rounded-full border-4 border-indigo-400/30" />
                <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-indigo-400 animate-spin" />
                <div className="absolute inset-2 rounded-full border-4 border-transparent border-t-blue-400 animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }} />
                <div className="absolute inset-0 flex items-center justify-center">
                  <svg className="w-8 h-8 text-indigo-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Validation Complete</h2>
            <p className="text-indigo-200 mb-4">Preparing your Risk Panel...</p>
            <div className="w-64 h-2 bg-indigo-900/50 rounded-full overflow-hidden mx-auto">
              <div className="h-full bg-gradient-to-r from-indigo-400 to-blue-400 rounded-full transition-all duration-[5000ms] ease-linear" style={{ width: `${transitionProgress}%` }} />
            </div>
          </div>
        </div>
      )}

      {/* Scan Complete popup */}
      {showValidationCelebration && (
        <ValidationCelebration
          passedCount={riskPanelPassedCount || validationSummary?.passes || 0}
          elementsScanned={scanStats.elements}
          rulesCompared={scanStats.rules}
          onClose={() => setShowValidationCelebration(false)}
          onViewResults={() => setActiveTab("risk")}
        />
      )}

      {/* Full-screen Risks Page overlay (keeps viewer mounted underneath) */}
      {showRisksPage && (
        <div className="fixed inset-0 bg-custom-light-50 z-50">
          <div className="h-full flex flex-col">
            {/* Header with back button */}
            <div className="bg-custom-navy border-b border-custom-dark shadow-sm">
              <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16">
                  <button
                    onClick={handleBackFromRisksPage}
                    className="flex items-center gap-2 px-4 py-2 text-white hover:text-custom-light-100 hover:bg-white hover:bg-opacity-10 rounded-lg transition-all duration-200"
                  >
                    <span>←</span> Back to BIM Viewer
                  </button>
                  <h1 className="text-xl font-semibold text-white">
                    Risk Management
                  </h1>
                  <div className="w-24"></div>
                </div>
              </div>
            </div>
            {/* RisksPage content */}
            <div className="flex-1 overflow-auto">
              <RisksPage />
            </div>
            <ToastContainer />
          </div>
        </div>
      )}

      {/* Celebration Page overlay (keeps viewer mounted underneath) */}
      {showCelebration && (
        <div className="fixed inset-0 bg-white z-50">
          <CelebrationPage onPreviousPage={handleBackFromCelebration} />
        </div>
      )}
    </div>
  );
};

export { BIMViewerStep };
